import type {
  ComponentProps,
  ComponentType,
  Dispatch,
  SetStateAction,
} from "react";
import { useRef, useState } from "react";
import { createContext, useContext } from "react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { useMobile } from "@/hooks/use-mobile";
import { LARGE_WIDTH_BREAKPOINT } from "@/utils/constants";
import { cn } from "@/utils/lib";

export enum DialogDrawerType {
  Dialog = "dialog",
  Drawer = "drawer",
}

type DialogDrawerContextValue = {
  type: DialogDrawerType;
  dialogDrawerState: {
    contentProps?: ComponentProps<typeof DialogContent> &
      ComponentProps<typeof DrawerContent>;
    rootProps?: ComponentProps<typeof Dialog> & ComponentProps<typeof Drawer>;
  };
  setDialogDrawerState: Dispatch<
    SetStateAction<DialogDrawerContextValue["dialogDrawerState"]>
  >;
};

export type DialogDrawerProps = ComponentProps<typeof Dialog> &
  ComponentProps<typeof Drawer> & {
    type?: DialogDrawerType;
  };

const DialogDrawerContext = createContext<DialogDrawerContextValue>(null!);

export const useDialogDrawerContext = () => {
  const context = useContext(DialogDrawerContext);
  if (!context) {
    throw new Error(
      "useDialogDrawerContext must be used within a DialogDrawerContext.Provider",
    );
  }
  return context;
};

type DialogDrawerState = {
  rootProps?: ComponentProps<typeof DialogDrawerRoot>;
};

type DialogDrawerOpenChangeHandler = (
  open: boolean,
  eventDetails?: unknown,
) => void;

export const DialogDrawer = ({
  type,
  open,
  onOpenChange,
  ...props
}: DialogDrawerProps) => {
  const { isMobile } = useMobile(LARGE_WIDTH_BREAKPOINT);
  const [dialogDrawerState, setDialogDrawerState] = useState<DialogDrawerState>(
    {},
  );
  const [internalOpen, setInternalOpen] = useState(open || false);
  const rootOpenChangeRef = useRef<DialogDrawerOpenChangeHandler | undefined>(
    dialogDrawerState?.rootProps?.onOpenChange as
      | DialogDrawerOpenChangeHandler
      | undefined,
  );
  const propOpenChangeRef = useRef<DialogDrawerOpenChangeHandler | undefined>(
    onOpenChange as DialogDrawerOpenChangeHandler | undefined,
  );
  rootOpenChangeRef.current = dialogDrawerState?.rootProps?.onOpenChange as
    | DialogDrawerOpenChangeHandler
    | undefined;
  propOpenChangeRef.current = onOpenChange as
    | DialogDrawerOpenChangeHandler
    | undefined;

  const finalOpen = dialogDrawerState?.rootProps?.open ?? open ?? internalOpen;

  return (
    <DialogDrawerContext.Provider
      value={{
        type:
          type ??
          (isMobile ? DialogDrawerType.Drawer : DialogDrawerType.Dialog),
        dialogDrawerState,
        setDialogDrawerState,
      }}
    >
      <DialogDrawerRoot
        {...props}
        open={finalOpen}
        onOpenChange={(open, eventDetails?: unknown) => {
          const rootOnOpenChange = rootOpenChangeRef.current;

          if (rootOnOpenChange) {
            void rootOnOpenChange(open, eventDetails);
            return;
          }

          setInternalOpen(open);
          propOpenChangeRef.current?.(open, eventDetails);
        }}
      />
    </DialogDrawerContext.Provider>
  );
};

const create =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  <DI extends ComponentType<any>, DR extends ComponentType<any>>(
    DialogComponent: DI,
    DrawerComponent: DR,
    customPropsNameFromState?: keyof DialogDrawerContextValue["dialogDrawerState"],
  ) => {
    const Component = (props: ComponentProps<DI> & ComponentProps<DR>) => {
      const { type, dialogDrawerState } = useDialogDrawerContext();
      // We remove onOpenChange, because it's handled on topLevel
      // @ts-expect-error it's fine
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { onOpenChange, ...customProps } =
        customPropsNameFromState && dialogDrawerState[customPropsNameFromState]
          ? dialogDrawerState[customPropsNameFromState]
          : {};
      const finalProps = {
        ...props,
        ...customProps,
        // @ts-expect-error we will assign all props
        className: cn(props.className, customProps?.className),
      };

      return type === DialogDrawerType.Dialog ? (
        // @ts-expect-error we will assign all props
        <DialogComponent {...finalProps} />
      ) : (
        // @ts-expect-error we will assign all props
        <DrawerComponent {...finalProps} />
      );
    };
    Component.displayName = `Polymorphic${DialogComponent.displayName}`;
    return Component;
  };

export const DialogDrawerRoot = create(Dialog, Drawer, "rootProps");

export const DialogDrawerClose = create(DialogClose, DrawerClose);

export const DialogDrawerContent = create(
  DialogContent,
  (
    props: ComponentProps<typeof DrawerContent> & {
      bodyClassName?: string;
      chrome?: "default" | "ghost";
      closeClassName?: string;
      hideOverlay?: boolean;
      portal?: boolean;
    },
  ) => {
    const { closeClassName, hideOverlay, portal, ...drawerProps } = props;
    void closeClassName;
    void hideOverlay;
    void portal;

    return (
      <DrawerContent
        {...drawerProps}
        onPointerDownCapture={(ev) => {
          const closestClickable = (ev.target as HTMLElement).closest(
            `
              button, a,
              [role='button'], [role='combobox'], [role='link'],
              [role='listbox'], [role='menuitem'], [role='option']
            `,
          );
          if (closestClickable) {
            ev.stopPropagation();
          }
        }}
      />
    );
  },
  "contentProps",
);

export const DialogDrawerDescription = create(
  DialogDescription,
  DrawerDescription,
);

export const DialogDrawerFooter = create(DialogFooter, DrawerFooter);

export const DialogDrawerHeader = create(DialogHeader, DrawerHeader);

export const DialogDrawerOverlay = create(DialogOverlay, DrawerOverlay);

export const DialogDrawerPortal = create(DialogPortal, DrawerPortal);

export const DialogDrawerTitle = create(DialogTitle, DrawerTitle);

export const DialogDrawerTrigger = create(DialogTrigger, DrawerTrigger);
